import type { BookPortalAppEnv } from '@lucimark/shared/helpers/bookPortalUrl'
import { resolveFeedsApiOrigin } from '@lucimark/shared/helpers/feedsApiUrl'
import {
  isRssDirectoryEntryPayloadUsable,
  rssDirectoryEntryLoadErrorMessage
} from '@lucimark/shared/helpers/rssFeedFetch'

const CACHE_TTL_MS = 5 * 60 * 1000

interface ICacheEntry {
  payload: TRssDirectoryEntryResponse
  expiresAt: number
}

const cache = new Map<string, ICacheEntry>()

const FEEDS_API_DEV_PROXY_PREFIX = '/feeds-api-proxy'

function resolveAppEnv(): BookPortalAppEnv {
  const mode = import.meta.env.MODE
  if (mode === 'staging') {
    return 'staging'
  }
  if (mode === 'production') {
    return 'production'
  }
  return 'development'
}

function feedsApiBaseUrl(): string {
  if (import.meta.env.DEV) {
    return FEEDS_API_DEV_PROXY_PREFIX
  }
  return resolveFeedsApiOrigin(resolveAppEnv(), import.meta.env.VITE_FEEDS_API_URL)
}

export interface TRssDirectoryEntryResponse {
  entryId: string
  label: string
  category: string
  provider: string
  feedUrl: string
  layoutId: import('@lucimark/shared/models/rssDirectory').TRssLayoutId
  layout: import('@lucimark/shared/models/playlist').TLayout
  layoutOptions: import('@lucimark/shared/models/rssDirectory').TRssLayoutOptions
  defaults: import('@lucimark/shared/models/rssDirectory').TRssDirectoryDefaults
  items: import('@lucimark/shared/models/rssDirectory').TRssParsedItem[]
  fetchedAt: string
  upstreamError?: string
}

function shouldCacheRssDirectoryEntry(payload: TRssDirectoryEntryResponse): boolean {
  return isRssDirectoryEntryPayloadUsable(payload)
}

export async function fetchRssDirectoryEntry(entryId: string): Promise<TRssDirectoryEntryResponse> {
  const hit = cache.get(entryId)
  if (hit != null && Date.now() < hit.expiresAt) {
    if (shouldCacheRssDirectoryEntry(hit.payload)) {
      return hit.payload
    }
    cache.delete(entryId)
  }

  const url = `${feedsApiBaseUrl()}/entries/${encodeURIComponent(entryId)}`
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) {
    throw new Error(`feeds api ${res.status}`)
  }
  const payload = await res.json() as TRssDirectoryEntryResponse
  if (shouldCacheRssDirectoryEntry(payload)) {
    cache.set(entryId, { payload, expiresAt: Date.now() + CACHE_TTL_MS })
  }
  return payload
}

export function peekCachedRssDirectoryEntry(entryId: string): TRssDirectoryEntryResponse | null {
  const hit = cache.get(entryId)
  if (hit != null && Date.now() < hit.expiresAt && shouldCacheRssDirectoryEntry(hit.payload)) {
    return hit.payload
  }
  if (hit != null) {
    cache.delete(entryId)
  }
  return null
}

export function rssDirectoryEntryResponseError(payload: TRssDirectoryEntryResponse): string | null {
  return rssDirectoryEntryLoadErrorMessage(payload)
}

export { feedsApiBaseUrl }
